import React from 'react';
import { Link } from 'react-router-dom';
import 'types/global';
import routes from 'routes';
import { convertDateTime, convertRecordToMilliseconds } from 'utils/time';
import { isPlacedByAge } from 'utils/groupLabel';
import { ReactComponent as StarIcon } from 'assets/images/icon_star.svg';
import { ReactComponent as ChartIcon } from 'assets/images/icon_chart.svg';
import { ReactComponent as ExternalLinkIcon } from 'assets/images/icon_arrow_right.svg';

/**
 * Generates a list of options for a dropdown or selection component from athlete data.
 *
 * This function processes an array of athlete objects and returns a formatted array
 * suitable for use in dropdown menus or selection components. Each option includes
 * a `label` (athlete's full name) and a `value` (athlete's unique ID).
 *
 * @param {Athlete[]} athletes - An array of athlete objects. Each object should include:
 *   - `firstName` (string): The first name of the athlete.
 *   - `lastName` (string): The last name of the athlete.
 *   - `athleteID` (string): A unique identifier for the athlete.
 * @returns {Array} - A list of options, each with:
 *   - `label` (string): The athlete's full name (e.g., "John Doe").
 *   - `value` (string|number): The unique ID of the athlete.
 *
 * @example
 * const athletes = [
 *   { firstName: "John", lastName: "Doe", athleteID: 1 },
 *   { firstName: "Jane", lastName: "Smith", athleteID: 2 },
 * ];
 * const options = generateAthleteOptions(athletes);
 * // Output:
 * // [
 * //   { label: "John Doe", value: 1 },
 * //   { label: "Jane Smith", value: 2 },
 * // ]
 *
 * @notes
 * - Assumes that each athlete object has valid `firstName`, `lastName`, and `athleteID` properties.
 * - Does not handle duplicate `athleteID` values; ensure uniqueness in the input data.
 */
export const generateAthleteOptions = (athletes) => {
  const options = [];

  athletes.forEach((athlete) => {
    options.push({
      label: `${athlete.firstName} ${athlete.lastName}`,
      value: athlete.athleteID,
    });
  });

  return options;
};

/**
 * Formats a date string into a readable format
 */
const formatDate = (dateString) => {
  const dateObj = new Date(dateString);
  const month = dateObj.toLocaleDateString('en-US', { month: 'long' });
  const day = dateObj.getDate().toString().padStart(2, '0');
  const year = dateObj.getFullYear().toString().slice(-2);
  return `${month} ${day}, ${year}`;
};

/**
 * Formats result values based on status and value
 * @param {Object} result - The result object containing resultValue, status, etc.
 * @returns {string} - Formatted result string
 */
const formatResultValue = (result) => {
  // If status is not OK, display the status
  if (result.status && result.status !== 'OK') {
    return result.status;
  }

  // If resultValue is 0 or "0" or "0.00", display "NR"
  if (
    result.resultValue === '0' ||
    result.resultValue === '0.00' ||
    result.resultValue === 0
  ) {
    return 'NR';
  }

  // Otherwise display the actual result value
  return result.resultValue;
};

/**
 * Formats best-performance values (season best, all-time PB)
 * @param {string} bestValue - The best value
 * @returns {string} - Formatted best value string
 */
const formatBestValue = (bestValue) => {
  // If bestValue is 0 or "0" or "0.00", display "NR"
  if (bestValue === '0' || bestValue === '0.00' || bestValue === 0) {
    return 'NR';
  }

  return bestValue;
};

/**
 * Processes recent result data to generate a formatted table-friendly structure.
 * Updated to use the new 5-column layout with improved UX.
 *
 * @param {Object} recentMeet - An object containing recent result data
 * @param {string|number} clubId - The unique identifier for the club.
 * @param {Function} openHistoryPopup - A callback function to open the history popup for a given event type.
 * @returns {Array} - A structured array of data objects formatted for a table.
 */
// A result placed by age links to the age view. An adult's result is placed in
// its own race, so it links to the event-round view. oldestPlacedByAge is the
// highest group number the club places by age (utils/groupLabel.js); the
// result's own number decides, so a past result links as it was placed.
const buildResultLink = (result, clubId, oldestPlacedByAge) => {
  if (isPlacedByAge(result.ageGroup, oldestPlacedByAge)) {
    // The page the result is on: its number, or its combined group's key
    // ("14-17", step 8), which the server sends as ageViewGroup.
    return routes.admin.ageView.url(
      clubId,
      result.meetId,
      result.eventType,
      result.ageViewGroup || result.ageGroup,
      result.gender,
      result.roundType
    );
  }
  return routes.admin.eventRound.url(
    clubId,
    result.meetId,
    result.eventType,
    result.eventId
  );
};

export const recentMeetData = (
  recentMeet,
  clubId,
  openHistoryPopup,
  oldestPlacedByAge
) => {
  const data = [];

  if (recentMeet?.results?.length > 0) {
    recentMeet.results.forEach((result) => {
      data.push({
        ...result,
        seasonPRValue: formatBestValue(result.seasonPRValue),
        pbValue: formatBestValue(result.pbValue),
        eventAndDate: (
          <Link
            to={buildResultLink(result, clubId, oldestPlacedByAge)}
            className="tableCellLink"
            style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
          >
            <div>
              <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                {result.eventType}
              </div>
              <div style={{ fontSize: '12px', color: '#6c757d' }}>
                {formatDate(recentMeet.meetDate)}
              </div>
            </div>
            <ExternalLinkIcon
              style={{ width: '14px', height: '14px', flexShrink: 0 }}
            />
          </Link>
        ),
        result: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatResultValue(result)}
            {result.isPR ? (
              <StarIcon style={{ marginLeft: '2px' }} title="Season Best" />
            ) : null}
          </div>
        ),
        seasonHistory: (
          <div
            className="tableCellLink"
            onClick={() => openHistoryPopup(result.eventType)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
            }}
          >
            <ChartIcon style={{ width: '16px', height: '16px' }} />
            <span>View Chart</span>
          </div>
        ),
      });
    });
  }
  return data;
};

/**
 * Processes past result data and transforms it into a structured format for display in a table.
 * Updated to use the new 5-column layout with improved UX.
 *
 * @param {Object} pastMeets - The object containing past result data.
 * @param {string} clubId - The ID of the club.
 * @param {Function} openHistoryPopup - A callback function to open the history popup for a specific event type.
 * @returns {Array} - An array of objects formatted for table rows.
 */
export const pastMeetsData = (
  pastMeets,
  clubId,
  openHistoryPopup,
  oldestPlacedByAge
) => {
  const data = [];

  if (pastMeets?.length > 0) {
    pastMeets.forEach((meet) => {
      meet.results.forEach((result) => {
        data.push({
          ...result,
          seasonPRValue: formatBestValue(result.seasonPRValue),
          pbValue: formatBestValue(result.pbValue),
          eventAndDate: (
            <Link
              to={buildResultLink(result, clubId, oldestPlacedByAge)}
              className="tableCellLink"
              style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
            >
              <div>
                <div style={{ fontWeight: '500', marginBottom: '2px' }}>
                  {result.eventType}
                </div>
                <div style={{ fontSize: '12px', color: '#6c757d' }}>
                  {convertDateTime(result.resultTimestamp).date}
                </div>
              </div>
              <ExternalLinkIcon
                style={{ width: '14px', height: '14px', flexShrink: 0 }}
              />
            </Link>
          ),
          result: (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {formatResultValue(result)}
              {result.isPR ? (
                <StarIcon style={{ marginLeft: '2px' }} title="Season Best" />
              ) : null}
            </div>
          ),
          seasonHistory: (
            <div
              className="tableCellLink"
              onClick={() => openHistoryPopup(result.eventType)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
              }}
            >
              <ChartIcon style={{ width: '16px', height: '16px' }} />
              <span>View Chart</span>
            </div>
          ),
        });
      });
    });
  }
  return data;
};

/**
 * Generates a history of event results sorted by timestamp for display or analysis.
 *
 * This function processes an array of event results, sorts them by the result timestamp in ascending order,
 * and creates a history array with formatted date and numeric record values.
 *
 * @param {Result[]} results - An array of result objects to process. Each object should have:
 *   - {string} resultTimestamp - The timestamp of the result (e.g., "2024-12-24T10:00:00").
 *   - {string} resultValue - The result value (e.g., "2:22.00", "14.65", "1.20").
 *   - {string} status - The status of the result (e.g., "OK", "DNF", "No Mark").
 *   - {string} eventType - The type of event (e.g., "800m", "High Jump", "Shot Put").
 *
 * @returns {Array} - An array of history objects, each containing:
 *   - {string} name - The formatted date of the result (e.g., "24 Dec 2024").
 *   - {number} record - The numeric value of the result.
 *   - {string} unit - The unit of measurement ('s' for time, 'm' for distance).
 */
export const createEventHistory = (results) => {
  const history = [];

  // Define field events (measured in meters/distance)
  const fieldEvents = [
    'High Jump',
    'Long Jump',
    'Triple Jump',
    'Pole Vault',
    'Shot Put',
    'Discus',
    'Hammer',
    'Javelin',
    'Vortex',
  ];

  results
    .filter((result) => {
      // Only include results with OK status and non-zero values
      return (
        result.status === 'OK' &&
        result.resultValue !== '0' &&
        result.resultValue !== '0.00' &&
        result.resultValue !== 0
      );
    })
    .sort((a, b) => new Date(a.resultTimestamp) - new Date(b.resultTimestamp))
    .forEach((result) => {
      try {
        const isFieldEvent = fieldEvents.includes(result.eventType);

        let recordValue;
        let unit;

        if (isFieldEvent) {
          // Field events: result is already in meters (e.g., "1.20", "5.20")
          recordValue = parseFloat(result.resultValue);
          unit = 'm';
        } else {
          // Track events: result is in time format, convert to seconds for chart
          recordValue = convertRecordToMilliseconds(result.resultValue) / 1000;
          unit = 's';
        }

        history.push({
          name: convertDateTime(result.resultTimestamp).date,
          record: recordValue,
          unit: unit,
        });
      } catch (error) {
        // Skip this result if there's an error converting it
        console.warn(
          'Skipping invalid result:',
          result.resultValue,
          result.eventType,
          error
        );
      }
    });

  return history;
};

/**
 * Retrieves the best personal record (PB) from a list of event results.
 *
 * This function processes an array of results, checking each one to determine if it's a personal record (PB).
 * It compares results with the same unit (meters or other) and returns the highest PB found.
 *
 * @param {Array<Object>} results - An array of result objects. Each object should have the following properties:
 *   - {boolean} isPR - A flag indicating whether the result is a personal record. (typeof: boolean)
 *   - {string} resultValue - The result value, which may include a unit (e.g., "10.5m"). (typeof: string)
 *
 * @returns {string|null} - The highest personal record in the format "value unit" (e.g., "10.5 m"), or null if no PB is found.
 */
export const getHistoryPR = (results) => {
  let resultPR = null;
  let resultType = '';
  results.forEach((result) => {
    if (result.isPR) {
      if (result.resultValue.includes('m')) {
        resultType = ' m';
        if (!resultPR || resultPR < +result.resultValue.replace(' m', '')) {
          resultPR = +result.resultValue.replace(' m', '');
        }
      } else {
        if (!resultPR || resultPR > +result.resultValue)
          resultPR = +result.resultValue;
      }
    }
  });

  if (!resultPR) return null;
  else return `${resultPR}${resultType}`;
};

/**
 * Transforms a list of historical results into a data structure suitable for display in a table.
 *
 * This function processes an array of result objects, formats the date, and includes the result value along with a
 * badge if it's a personal record (PB).
 *
 * @param {Array<Object>} results - An array of result objects. Each object should have the following properties:
 *   - {string} resultTimestamp - The timestamp when the result was recorded. (typeof: string)
 *   - {string} resultValue - The value of the result, such as time or distance. (typeof: string)
 *   - {boolean} isPR - A flag indicating whether the result is a personal record. (typeof: boolean)
 *
 * @returns {Array<Object>} - An array of objects containing:
 *   - `date`: The formatted date string of the result (typeof: string).
 *   - `result`: The result value, possibly with a PB badge (typeof: JSX.Element).
 */
export const historyResultData = (results) => {
  const data = [];

  if (results.length > 0) {
    const sorted = [...results].sort(
      (a, b) => new Date(b.resultTimestamp) - new Date(a.resultTimestamp),
    );
    sorted.forEach((result) => {
      data.push({
        ...result,
        date: convertDateTime(result.resultTimestamp).date,
        result: (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {formatResultValue(result)}
            {result.isPR ? (
              <StarIcon style={{ marginLeft: '2px' }} title="Season Best" />
            ) : null}
          </div>
        ),
      });
    });
  }

  return data;
};
